import { memo, useCallback, useState, type MouseEvent } from "react";
import { NavLink, useNavigate } from "react-router";
import { useTranslation } from "react-i18next";
import { ArrowRight, ArrowUpRight, Sparkles } from "lucide-react";
import { useKindeAuth } from "@kinde-oss/kinde-auth-react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/shared/components/ui/popover";
import NotificationIcon from "@/shared/icons/notification.svg?react";
import { useUser } from "@/entities/user";
import { hasUnreadNews, LATEST_NEWS_ITEM, tNewsItem, useMarkNewsSeen } from "@/entities/news";
import { toIntlLocale } from "@/shared/lib/appLocale";
import { resetStates } from "@/shared/lib/utils";

export const NewsBadge = memo(function NewsBadge() {
  const { user: kindeUser } = useKindeAuth();
  const { user } = useUser();
  const { t, i18n } = useTranslation("news");
  const navigate = useNavigate();
  const { mutate: markSeen } = useMarkNewsSeen();
  const [open, setOpen] = useState(false);
  const latest = LATEST_NEWS_ITEM;
  const unread = Boolean(user) && hasUnreadNews(user?.lastSeenNewsAt);
  const locale = toIntlLocale(i18n.resolvedLanguage ?? i18n.language);

  const handleOpenChange = useCallback(
    (nextOpen: boolean) => {
      setOpen(nextOpen);
      if (nextOpen && unread) {
        markSeen();
      }
    },
    [markSeen, unread]
  );

  const handleViewAllClick = useCallback(
    (event: MouseEvent<HTMLAnchorElement>) => {
      resetStates();
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) {
        setOpen(false);
        return;
      }
      event.preventDefault();
      setOpen(false);
      navigate("/news");
    },
    [navigate]
  );

  if (!latest || !kindeUser) {
    return null;
  }

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={t("badge.ariaLabel")}
          className="relative flex aspect-square cursor-pointer items-center justify-center rounded-[20px] border-0 bg-transparent p-3 glass-effect outline-none focus-visible:ring-2 focus-visible:ring-white/40"
        >
          <NotificationIcon className="size-4 text-white" aria-hidden="true" />
          {unread ? (
            <span
              className="absolute top-0 right-0 size-3 rounded-full bg-[#9D7BFF]"
              aria-hidden="true"
            />
          ) : null}
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        onCloseAutoFocus={(event) => event.preventDefault()}
        className="w-80 border-white/10 bg-[#39326e] p-4 text-white data-[state=closed]:animate-none"
      >
        <p className="text-[12px] font-body uppercase tracking-wide text-[#C1BFCA]">
          {t("badge.title")}
        </p>

        <a
          href={`/news/${latest.id}`}
          target="_blank"
          rel="noopener noreferrer"
          className="group relative mt-3 block overflow-hidden rounded-2xl border border-white/10 bg-linear-to-br from-white/10 via-white/5 to-transparent p-4 transition-colors duration-300 hover:border-[#D4B9ED]/40 hover:from-white/15 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#D4B9ED]/60"
        >
          <span
            aria-hidden="true"
            className="pointer-events-none absolute -top-12 -right-12 size-32 rounded-full bg-[#835EFF]/30 blur-2xl transition-opacity duration-300 group-hover:opacity-100"
          />
          <span className="relative flex items-center gap-2 text-[11px] font-body uppercase tracking-[0.08em] text-[#D4B9ED]">
            <Sparkles className="size-3.5 shrink-0" aria-hidden="true" />
            {t(`types.${latest.type}`)}
            <span aria-hidden="true" className="text-white/30">
              •
            </span>
            <time
              dateTime={latest.publishedAt}
              className="normal-case tracking-normal text-[#C1BFCA]"
            >
              {new Date(latest.publishedAt).toLocaleDateString(locale, {
                dateStyle: "long",
              })}
            </time>
            <span className="ml-auto flex size-7 shrink-0 items-center justify-center rounded-full border border-white/10 bg-white/5 transition-colors duration-300 group-hover:border-[#D4B9ED]/50 group-hover:bg-[#835EFF]/40">
              <ArrowUpRight
                className="size-3.5 text-white transition-transform duration-300 group-hover:-translate-y-px group-hover:translate-x-px"
                aria-hidden="true"
              />
            </span>
          </span>
          <span className="relative mt-2 block font-heading text-[18px] font-medium leading-snug text-white">
            {tNewsItem(t, latest.id, "badgeTitle")}
          </span>
          <span className="relative mt-1.5 block text-[13px] leading-relaxed text-[#C1BFCA]">
            {tNewsItem(t, latest.id, "badgeTeaser")}
          </span>
        </a>

        <NavLink
          to="/news"
          onClick={handleViewAllClick}
          className="group relative mt-4 flex w-full items-center justify-center gap-2 overflow-hidden rounded-full bg-linear-to-r from-[#835EFF] to-[#A78BFA] px-4 py-2.5 text-[14px] font-body font-medium text-white shadow-[0_8px_24px_-8px_rgba(131,94,255,0.7)] transition-[box-shadow,transform] duration-300 hover:shadow-[0_10px_28px_-6px_rgba(157,123,255,0.75)] active:scale-[0.98] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/50"
        >
          <span
            aria-hidden="true"
            className="absolute inset-0 bg-linear-to-r from-[#9D7BFF] to-[#E8A0BF] opacity-0 transition-opacity duration-500 ease-out group-hover:opacity-100"
          />
          <span className="relative">{t("badge.viewAll")}</span>
          <ArrowRight
            className="relative size-4 transition-transform duration-300 ease-out group-hover:translate-x-0.5"
            aria-hidden="true"
          />
        </NavLink>
      </PopoverContent>
    </Popover>
  );
});
